/**
 * post-V9 Section 0 — DXF import memory profiling instrumentation.
 *
 * Adds `performance.measure` spans on each stage of the import pipeline so
 * Iteration 0 baseline (10k / 50k / 100k synthetic DXF) can attribute time +
 * memory growth to specific stages. Used by `scripts/section0-baseline.mjs`
 * via `performance.getEntriesByType("measure")`.
 *
 * The instrumentation is **always-on** but cheap: each call is ~100ns
 * (mark + measure on a perf timeline that the browser otherwise ignores).
 * Detached buffers are not retained — we don't push to PerformanceObserver
 * here; the runner reads via `getEntriesByType` after the import settles.
 *
 * Stage names (kept stable so the runner's table doesn't drift):
 *   - section0:worker:parse        — full worker `runParse[File]` body
 *   - section0:worker:decode       — `await file.text()` (file path only)
 *   - section0:worker:tokenize     — `parseDxfDocumentStreaming`
 *   - section0:worker:bridge       — `importDxfDocumentToDrawingDocument`
 *   - section0:worker:postMessage  — `workerScope.postMessage(result)` send
 *   - section0:client:receive      — main thread receives `result` message
 *   - section0:client:cachePopulate — populate per-space caches for huge-import path
 *   - section0:client:compactCachePopulate — `populateCompactCache` in huge-import path
 *   - section0:client:metadataIndex — `entityMetadataIndex.bulkLoad`
 *   - section0:client:setState     — final `setState` commit (geometryDocument + sync state)
 *
 * `mark` returns the start time so callers wrap their work in
 * `try { ... } finally { measureSince(name, t0); }` to ensure the span is
 * recorded even on throw.
 */

const NS = "section0:";

/**
 * Returns true if the runtime exposes `performance.mark/measure`. Web
 * Workers have `performance` but vitest's jsdom may not expose `measure`,
 * so probe both. Cached on first call.
 */
let supported: boolean | null = null;
function isPerfSupported(): boolean {
  if (supported !== null) return supported;
  supported =
    typeof performance !== "undefined" &&
    typeof performance.mark === "function" &&
    typeof performance.measure === "function";
  return supported;
}

/** Stamp `<NS><stage>:start`. Returns the wall-clock timestamp for fallback. */
export function markStart(stage: string): number {
  const t0 = isPerfSupported() ? performance.now() : Date.now();
  if (isPerfSupported()) {
    try {
      performance.mark(`${NS}${stage}:start`);
    } catch {
      /* timeline full — silently drop */
    }
  }
  return t0;
}

/**
 * End span by emitting `<NS><stage>` with the start mark recorded by
 * `markStart`. The matching mark is cleared so repeat imports don't pile
 * up names (Chrome will throw "duplicate mark" otherwise).
 */
export function measureSince(stage: string, _t0: number): void {
  if (!isPerfSupported()) return;
  const startMark = `${NS}${stage}:start`;
  try {
    performance.measure(`${NS}${stage}`, startMark);
  } catch {
    /* mark missing or already cleared — drop */
  }
  try {
    performance.clearMarks(startMark);
  } catch {
    /* ignore */
  }
}

/**
 * Convenience wrapper: mark→measure around a sync function. Also returns
 * the result so caller chains: `const x = traced("foo", () => compute())`.
 */
export function traced<T>(stage: string, fn: () => T): T {
  const t0 = markStart(stage);
  try {
    return fn();
  } finally {
    measureSince(stage, t0);
  }
}

/** Async variant of `traced`. */
export async function tracedAsync<T>(
  stage: string,
  fn: () => Promise<T>,
): Promise<T> {
  const t0 = markStart(stage);
  try {
    return await fn();
  } finally {
    measureSince(stage, t0);
  }
}

/**
 * Snapshot all `section0:*` measures collected so far. Runner calls this
 * via `page.evaluate(...)` after the import settles. The browser keeps
 * the entries on the timeline indefinitely (we don't clear) so re-reads
 * are idempotent within a single page lifetime.
 */
export function getSection0Measures(): Array<{
  name: string;
  startTime: number;
  duration: number;
}> {
  if (!isPerfSupported()) return [];
  const entries = performance.getEntriesByType("measure");
  const out: Array<{ name: string; startTime: number; duration: number }> = [];
  for (const e of entries) {
    if (e.name.startsWith(NS)) {
      out.push({
        name: e.name.slice(NS.length),
        startTime: e.startTime,
        duration: e.duration,
      });
    }
  }
  return out;
}

/** Wipe all `section0:*` measures. Runner uses this between fixture runs. */
export function clearSection0Measures(): void {
  if (!isPerfSupported()) return;
  for (const e of performance.getEntriesByType("measure")) {
    if (e.name.startsWith(NS)) {
      try {
        performance.clearMeasures(e.name);
      } catch {
        /* ignore */
      }
    }
  }
}

/**
 * Iteration 1 — Hypothesis A test: DrawingEntity payload size.
 *
 * Plan §Iteration 1 hypothesizes ~50KB/entity from DrawingEntity payload size.
 * This helper samples up to N entities from the imported drawing and computes
 * the average JSON byte size, plus min/max/median. Sampling is done after
 * import settles so we only count entities currently retained in the
 * reactive store. post-V9 Section 0 Iter 6.D — slim-state-as-pointer
 * (Phase 3b PR #6) keeps `space.entities` populated for huge imports too,
 * so the legacy `__entitiesCache` Map source is no longer needed here.
 *
 * Results are returned and also published on `globalThis.__section0_entitySampling`
 * so the out-of-process runner can read them via `page.evaluate(...)` without
 * round-tripping the (potentially huge) entity array itself.
 *
 * Methodology notes:
 *   - `JSON.stringify` is the established proxy for retained heap size in
 *     V8 — actual heap retention is typically 1.5×-3× JSON byte size due to
 *     Map/Set layout, hidden classes and pointer overhead, but JSON length
 *     is reliable for ranking & relative measurement.
 *   - We sample at most `sampleSize` entities; for huge files this avoids
 *     blocking the main thread on stringify of millions of objects.
 *   - First-N sampling is biased to the first space's first entities; for
 *     synthetic fixtures (uniform LINE+CIRCLE) the bias is negligible.
 */
export interface EntitySamplingResult {
  totalEntitiesSeen: number;
  sampleSize: number;
  totalBytes: number;
  avgBytes: number;
  minBytes: number;
  maxBytes: number;
  medianBytes: number;
  // Type histogram for the sample (e.g. `{ "line": 50, "circle": 50 }`).
  typeHistogram: Record<string, number>;
}

interface SpaceLike {
  entities?: unknown[];
}
interface StoreLike {
  geometryDocument?: { spaces?: SpaceLike[] };
}

export function sampleEntitySizes(
  sampleSize = 100,
): EntitySamplingResult | null {
  if (typeof globalThis === "undefined") return null;
  const g = globalThis as unknown as {
    __useEditorStore?: { getState: () => StoreLike };
  };
  const store = g.__useEditorStore?.getState?.();
  if (!store) return null;
  const spaces = store.geometryDocument?.spaces ?? [];

  // post-V9 Section 0 Iter 6.D — `space.entities` is the only entity
  // source after dropping the legacy `__entitiesCache` Map (slim-state-
  // as-pointer keeps the array populated for huge imports too). The
  // dedup-by-reference Set is preserved as defensive cheap insurance
  // against accidental double-iteration if this helper is ever extended
  // to read additional sources.
  const seen = new Set<unknown>();
  const collected: unknown[] = [];
  let totalSeen = 0;
  const collect = (e: unknown): void => {
    if (seen.has(e)) return;
    seen.add(e);
    totalSeen++;
    if (collected.length < sampleSize) collected.push(e);
  };
  for (const space of spaces) {
    for (const e of space.entities ?? []) collect(e);
  }
  if (collected.length === 0) return null;

  const sizes: number[] = [];
  const typeHistogram: Record<string, number> = {};
  let totalBytes = 0;
  for (const e of collected) {
    let bytes = 0;
    try {
      bytes = JSON.stringify(e).length;
    } catch {
      bytes = 0;
    }
    sizes.push(bytes);
    totalBytes += bytes;
    const t = (e as { type?: string })?.type ?? "unknown";
    typeHistogram[t] = (typeHistogram[t] ?? 0) + 1;
  }
  sizes.sort((a, b) => a - b);
  const avgBytes = Math.round(totalBytes / sizes.length);
  const median = sizes[Math.floor(sizes.length / 2)] ?? 0;
  const result: EntitySamplingResult = {
    totalEntitiesSeen: totalSeen,
    sampleSize: sizes.length,
    totalBytes,
    avgBytes,
    minBytes: sizes[0] ?? 0,
    maxBytes: sizes[sizes.length - 1] ?? 0,
    medianBytes: median,
    typeHistogram,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__section0_entitySampling = result;
  return result;
}

/**
 * Iteration 2 — Hypothesis E test: typed-array compact representation.
 *
 * Side-by-side measurement of standard `LineEntity` records vs. compact
 * typed-array store. Builds a compact store from all LINE entities
 * currently in the reactive `geometryDocument.spaces` (post-V9 Section 0
 * Iter 6.D — slim-state-as-pointer keeps that array populated for huge
 * imports too, replacing the legacy `__entitiesCache` Map source) and
 * measures the V8 heap delta around the build.
 *
 * Returns:
 *   - `lineCount` — total LINE entities found
 *   - `compactStoreSize` — coarse byte-size of the compact store
 *     (typed-array byteLengths + string-table chars)
 *   - `heapBefore` / `heapAfter` — `performance.memory.usedJSHeapSize`
 *     snapshots around the build (with GC if available)
 *   - `compactBytesPerLine` — derived per-entity heap retention for the
 *     compact representation, comparable to the Iter 1
 *     "Per-entity heap (Δ / count)" figure.
 *
 * Stores the compact store on `globalThis.__section0_compactLineStore`
 * for the duration of the page so the runner can perform a second
 * measurement after deferred GC.
 */
export interface CompactLineMeasurement {
  lineCount: number;
  compactStoreBytes: number;
  compactBytesPerLine: number;
  heapBefore: number | null;
  heapAfter: number | null;
  heapDelta: number | null;
  /**
   * `Δheap / lineCount`. Comparable to Iter 1's per-entity heap figure.
   */
  heapPerLine: number | null;
  /** Number of unique strings interned across the compact store. */
  stringTableSize: number;
  /** Sum of all interned string char counts. */
  stringTableChars: number;
}

interface SpaceEntitiesLike {
  entities?: unknown[];
}
interface StoreWithDocument {
  geometryDocument?: { spaces?: SpaceEntitiesLike[] };
}

/**
 * Trigger GC-best-effort. Returns immediately if `--js-flags=--expose-gc`
 * was not passed to the browser; the runner script always sets that flag.
 */
function tryGc(): void {
  const w = globalThis as unknown as { gc?: () => void };
  if (typeof w.gc === "function") {
    try {
      w.gc();
    } catch {
      /* ignore */
    }
  }
}

function readUsedHeap(): number | null {
  const m = (
    performance as unknown as {
      memory?: { usedJSHeapSize: number };
    }
  ).memory;
  return m ? m.usedJSHeapSize : null;
}

export async function measureCompactLineRepresentation(): Promise<CompactLineMeasurement | null> {
  if (typeof globalThis === "undefined") return null;
  // Lazy import — keeps the compact store out of the main editor bundle
  // unless instrumentation is exercised. Safe in vite/esbuild because the
  // path is statically resolvable.
  const { compactStoreFromEntities } = await import("./compactEntityStore");

  const g = globalThis as unknown as {
    __useEditorStore?: { getState: () => StoreWithDocument };
  };

  // post-V9 Section 0 Iter 6.D — read only from reactive
  // `geometryDocument.spaces[].entities`. Slim-state-as-pointer (Phase 3b
  // PR #6) keeps that array populated for huge imports too, so the
  // legacy `__entitiesCache` Map iteration that previously paired here
  // for dedup is no longer relevant. Dedup-by-reference is preserved as
  // defensive cheap insurance.
  const seen = new Set<unknown>();
  const lines: unknown[] = [];
  const collect = (e: unknown): void => {
    if ((e as { type?: string }).type !== "Line") return;
    if (seen.has(e)) return;
    seen.add(e);
    lines.push(e);
  };
  const store = g.__useEditorStore?.getState?.();
  const spaces = store?.geometryDocument?.spaces ?? [];
  for (const space of spaces) {
    for (const e of space.entities ?? []) collect(e);
  }
  if (lines.length === 0) return null;

  // Heap before build (post-GC).
  tryGc();
  await new Promise((r) => setTimeout(r, 50));
  tryGc();
  const heapBefore = readUsedHeap();

  // Build compact store.
  const compact = compactStoreFromEntities(lines);

  // Heap after build (post-GC). The standard records still exist in the
  // cache; what we measure here is the *additional* cost of the compact
  // store. To compare apples-to-apples, runner subtracts this from the
  // Iter 1 standard heap-per-entity baseline.
  tryGc();
  await new Promise((r) => setTimeout(r, 50));
  tryGc();
  const heapAfter = readUsedHeap();

  // Coarse byte-size attributable to the compact store itself:
  //   - typed-array buffer byteLengths
  //   - string-table: char count × 2 (UTF-16) + overhead estimate
  //   - id / spaceId: each LINE has 2 string slots whose pointers we
  //     count via 16 B per slot (V8 64-bit pointer + tag).
  const taBytes =
    compact.floats.byteLength +
    compact.styles.byteLength +
    compact.arrayModes.byteLength;
  let stringChars = 0;
  for (const s of compact.stringTable) stringChars += s.length;
  let idChars = 0;
  for (let i = 0; i < compact.count; i++) {
    idChars += compact.ids[i]!.length;
    idChars += compact.spaceIds[i]!.length;
  }
  // V8 string body: ~16 B header + 2 B/char (UTF-16) for "regular" strings.
  // Pointer slots for ids/spaceIds in the parallel arrays: 8 B each on 64-bit.
  const stringBodyBytes = (stringChars + idChars) * 2 + (compact.count * 2 + compact.stringTable.length) * 16;
  const ptrBytes = compact.count * 2 * 8;
  const compactStoreBytes = taBytes + stringBodyBytes + ptrBytes;

  const heapDelta =
    heapBefore != null && heapAfter != null ? heapAfter - heapBefore : null;
  const heapPerLine =
    heapDelta != null && lines.length > 0 ? heapDelta / lines.length : null;

  // Store on globalThis for runner inspection.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__section0_compactLineStore = compact;

  return {
    lineCount: compact.count,
    compactStoreBytes,
    compactBytesPerLine: compact.count > 0 ? compactStoreBytes / compact.count : 0,
    heapBefore,
    heapAfter,
    heapDelta,
    heapPerLine,
    stringTableSize: compact.stringTable.length,
    stringTableChars: stringChars,
  };
}
